import { cva } from "class-variance-authority";
import { toast as sonnerToast } from "sonner";
import { cn } from "~/lib/utils";
import Image from "~/components/atoms/Image";
import iconCloseSmall from "~/assets/icon-close-small.svg";

type ToastTypes = "success" | "error" | "info" | "warning";

// eslint-disable-next-line tailwindcss/no-custom-classname
const variants = cva("toast group flex-1 group-[.toaster]:text-gray-80", {
    variants: {
        type: {
            success: ["group-[.toaster]:bg-accent-green"],
            error: ["group-[.toaster]:bg-[#f4c5c5]"],
            info: ["group-[.toaster]:bg-white"],
            warning: ["group-[.toaster]:bg-[#ffe46b]"],
        },
    },
    defaultVariants: {
        type: "info",
    },
});

interface ToastProps {
    id: number | string;
    type: ToastTypes;
    content: React.ReactNode;
    closeButton?: boolean;
}

function Toast({ content, id, type, closeButton }: ToastProps) {
    const classNames = cn(
        "py-2.5 px-[15px] text-base font-normal rounded-[10px] shadow-[0px_2px_6px_0px_rgba(148,148,148,0.50)] border border-blue-20 justify-start items-center gap-[15px] inline-flex",
        variants({ type }),
    );
    const closeButtonClasses = "flex-none";
    return (
        <div className={classNames}>
            {content}
            {closeButton && (
                <button onClick={() => sonnerToast.dismiss(id)} className={closeButtonClasses}>
                    <Image src={iconCloseSmall} alt="Close Icon" height={15} width={15} />
                </button>
            )}
        </div>
    );
}

interface CustomToastOptions {
    duration?: number;
    onDismiss?: (t: { id: number | string }) => void;
    onAutoClose?: (t: { id: number | string }) => void;
}

export const toast = (
    toastProps: Omit<ToastProps, "id">,
    options?: CustomToastOptions,
): number | string =>
    sonnerToast.custom(
        id => (
            <Toast
                id={id}
                type={toastProps.type}
                content={toastProps.content}
                closeButton={toastProps.closeButton}
            />
        ),
        options,
    );

export default Toast;
